// ============================================================
// IN-APP TEXT PROMPT (replaces window.prompt for Electron/APK)
// ============================================================
//
// Electron with sandbox:true and Capacitor WebView both silently
// return null from window.prompt(). This module provides a DOM-
// based overlay prompt that works everywhere: a semi-transparent
// backdrop + centered panel with title, input, OK and Cancel.
//
// Usage:
//   const val = await showPrompt('Title', 'default');
//   if (val === null) { /* cancelled */ }



/** Currently active prompt element, if any. */
let activeOverlay: HTMLDivElement | null = null;

/**
 * Show a modal text input prompt overlaid on the game canvas.
 * Returns the trimmed input string, or null if cancelled.
 */
export function showPrompt(
  title: string,
  defaultValue = '',
): Promise<string | null> {
  // Prevent stacking — dismiss any existing prompt first.
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }

  return new Promise((resolve) => {
    // --- Overlay backdrop ---
    const overlay = document.createElement('div');
    activeOverlay = overlay;
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0', left: '0', width: '100%', height: '100%',
      background: 'rgba(4,6,20,0.82)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '9999',
      fontFamily: "'Segoe UI', sans-serif",
      touchAction: 'none',
    });

    // --- Dialog panel ---
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      background: '#0e1328',
      border: '2px solid #335588',
      borderRadius: '12px',
      padding: '22px 20px 18px',
      width: '90%',
      maxWidth: '360px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    });

    // Title
    const titleEl = document.createElement('div');
    titleEl.textContent = title;
    Object.assign(titleEl.style, {
      color: '#ccddee',
      fontSize: '15px',
      fontWeight: 'bold',
      marginBottom: '14px',
      textAlign: 'center',
      whiteSpace: 'pre-line',
      lineHeight: '1.45',
    });
    panel.appendChild(titleEl);

    // Input
    const input = document.createElement('input');
    input.type = 'text';
    input.value = defaultValue;
    Object.assign(input.style, {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'monospace',
      background: '#141c35',
      border: '1px solid #445577',
      borderRadius: '6px',
      color: '#eef2ff',
      outline: 'none',
      boxSizing: 'border-box',
      marginBottom: '16px',
    });
    input.addEventListener('focus', () => {
      input.style.borderColor = '#5588cc';
    });
    input.addEventListener('blur', () => {
      input.style.borderColor = '#445577';
    });
    panel.appendChild(input);

    // Button row
    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, {
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
    });

    function makeBtn(label: string, color: string, bgHover: string): HTMLButtonElement {
      const btn = document.createElement('button');
      btn.textContent = label;
      Object.assign(btn.style, {
        flex: '1',
        padding: '10px 0',
        fontSize: '14px',
        fontWeight: 'bold',
        background: '#1a2545',
        border: `2px solid ${color}`,
        borderRadius: '6px',
        color: color,
        cursor: 'pointer',
        fontFamily: "'Segoe UI', sans-serif",
        outline: 'none',
        touchAction: 'manipulation',
      });
      btn.addEventListener('pointerover', () => { btn.style.background = bgHover; });
      btn.addEventListener('pointerout', () => { btn.style.background = '#1a2545'; });
      return btn;
    }

    const cancelBtn = makeBtn('Cancel', '#ff8866', '#2a1520');
    const okBtn = makeBtn('OK', '#44ccff', '#15253a');

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(okBtn);
    panel.appendChild(btnRow);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Focus the input after a microtask so it receives focus reliably.
    setTimeout(() => { input.focus(); input.select(); }, 30);

    // --- Event handlers ---
    function finish(value: string | null): void {
      if (!activeOverlay) return;   // already resolved
      overlay.remove();
      activeOverlay = null;
      resolve(value);
    }

    okBtn.addEventListener('click', () => finish(input.value.trim()));
    cancelBtn.addEventListener('click', () => finish(null));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') finish(input.value.trim());
      if (e.key === 'Escape') finish(null);
    });
    // Clicking backdrop = cancel.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) finish(null);
    });
  });
}
